import React from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/Footer';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import SEO from 'components/SEO';
import FAQ from 'components/ui/FAQ';
import RelatedBlogs from 'components/RelatedBlogs';
import { workflowAutomationFAQs } from '../data/faqs';
import { useNavigate } from 'react-router-dom';

const WorkflowAutomation = () => {
  const navigate = useNavigate();

  const seoData = {
    title: 'Workflow Automation & Integrations Services - n8n, Zapier, Make | OctalChip',
    description: 'Professional workflow automation services using n8n, Zapier, and Make. We design, build, and optimize automated workflows to connect your tools, eliminate manual work, and improve operational efficiency.',
    keywords: 'workflow automation services, n8n automation, zapier integration, make automation, business process automation, workflow integration, automation services, process automation, business automation, integration services',
    canonical: 'https://www.octalchip.com/services/workflow-automation',
    faqData: workflowAutomationFAQs,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Workflow Automation & Integrations Services",
      "description": "Professional workflow automation services using n8n, Zapier, and Make to connect tools and automate business processes",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip"
      }
    }
  };

  const serviceCards = [
    {
      title: 'n8n Automation',
      description: 'Custom, self-hosted workflow automation for businesses that need full control, security, and scalability.',
      icon: 'Settings',
      path: '/services/n8n-automation',
      features: ['Self-hosted', 'Full Control', 'Enterprise Security', 'Scalable'],
      gradient: 'from-[#00d690] to-[#00b876]'
    },
    {
      title: 'Zapier Integrations',
      description: 'Fast and reliable integrations between popular SaaS tools for sales, marketing, and operations.',
      icon: 'Zap',
      path: '/services/zapier-integration',
      features: ['Fast Setup', 'Reliable', 'Popular Tools', 'Easy to Use'],
      gradient: 'from-[#00b876] to-[#00d690]'
    },
    {
      title: 'Make (Integromat) Automation',
      description: 'Advanced visual workflows with complex logic, routing, and data transformation.',
      icon: 'Settings',
      path: '/services/make-automation',
      features: ['Complex Logic', 'Visual Workflows', 'Data Transformation', 'Advanced Routing'],
      gradient: 'from-[#00d690] via-[#00b876] to-[#00d690]'
    }
  ];

  const whatWeAutomate = [
    'CRM systems',
    'Email marketing platforms',
    'AI APIs (OpenAI, Claude, etc.)',
    'Databases & spreadsheets',
    'Payment gateways',
    'Web applications',
    'Internal dashboards',
    'Customer support tools'
  ];

  const whyOctalChip = [
    'Automation-first mindset',
    'Secure and scalable architectures',
    'API-driven integrations',
    'AWS & cloud expertise',
    'AI + Automation specialization',
    'Long-term support & optimization'
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Hero Section - Asymmetric Design */}
      <section id="hero" className="relative py-12 md:py-20 lg:py-28 overflow-hidden">
        {/* Geometric Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#00d690]/5 via-white to-[#00d690]/10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#00d690]/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00b876]/10 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        {/* Diagonal Accent Lines */}
        <div className="absolute top-20 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00d690]/30 to-transparent transform rotate-12"></div>
        <div className="absolute bottom-20 right-0 w-full h-px bg-gradient-to-l from-transparent via-[#00b876]/30 to-transparent transform -rotate-12"></div>

        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Left Side - Content */}
              <div className="space-y-6 md:space-y-8">
                <div className="inline-flex items-center gap-3 px-4 py-2 bg-[#00d690]/10 rounded-full border border-[#00d690]/20">
                  <div className="w-2 h-2 bg-[#00d690] rounded-full animate-pulse"></div>
                  <span className="text-sm font-semibold text-[#00d690]">Workflow Automation</span>
                </div>
                
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                  <span className="text-gray-800">Automation That</span>
                  <br />
                  <span className="bg-gradient-to-r from-[#00d690] to-[#00b876] bg-clip-text text-transparent">
                    Scales Your Business
                  </span>
                </h1>
                
                <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                  We design, build, and optimize automated workflows using n8n, Zapier, and Make to connect your tools, 
                  eliminate manual work, and improve operational efficiency.
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <Button 
                    variant="primary" 
                    onClick={() => navigate('/contact#contact-form')}
                    className="px-8 py-4 text-base shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                  >
                    Discuss Automation
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/services#services')}
                    className="px-8 py-4 text-base border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
                  >
                    View Automation Services
                  </Button>
                </div>
              </div>

              {/* Right Side - Visual Element */}
              <div className="relative hidden md:block">
                <div className="relative w-full aspect-square">
                  {/* Hexagonal Pattern */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-64 h-64 bg-gradient-to-br from-[#00d690]/20 to-[#00b876]/20 rounded-3xl transform rotate-45"></div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-48 h-48 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-2xl transform -rotate-12 shadow-2xl"></div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <div className="w-32 h-32 bg-white rounded-xl shadow-xl flex items-center justify-center">
                      <Icon name="Settings" size={64} className="text-[#00d690]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer Section - Hexagonal Cards */}
      <section id="what-we-offer" className="py-16 md:py-24 relative">
        <div className="absolute inset-0 bg-gray-50 -skew-y-1"></div>
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              What We Offer
            </h2>
            <div className="inline-block w-24 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive automation solutions tailored to your business needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10 max-w-7xl mx-auto">
            {serviceCards.map((service, index) => (
              <div 
                key={index} 
                className="group relative"
              >
                {/* Card with Hexagonal Accent */}
                <div className="relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border-2 border-transparent hover:border-[#00d690]/30">
                  {/* Top Hexagonal Accent */}
                  <div className={`absolute -top-4 left-1/2 transform -translate-x-1/2 w-16 h-16 bg-gradient-to-br ${service.gradient} rounded-xl rotate-45 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <div className="transform -rotate-45">
                      <Icon name={service.icon} size={32} className="text-white" />
                    </div>
                  </div>
                  
                  <div className="pt-8 space-y-4">
                    <h3 className="text-2xl font-bold text-gray-800 group-hover:text-[#00d690] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 pt-2">
                      {service.features.map((feature, idx) => (
                        <span key={idx} className="px-3 py-1 bg-[#00d690]/10 text-[#00d690] rounded-full text-sm font-medium border border-[#00d690]/20">
                          {feature}
                        </span>
                      ))}
                    </div>
                    
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (service.path === '/services/n8n-automation') {
                          navigate('/services/n8n-automation#hero');
                        } else if (service.path === '/services/zapier-integration') {
                          navigate('/services/zapier-integration#hero');
                        } else if (service.path === '/services/make-automation') {
                          navigate('/services/make-automation#hero');
                        } else {
                          navigate(service.path);
                        }
                      }}
                      className="w-full mt-6 border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
                    >
                      Explore {service.title}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Automate Section - Diagonal Layout */}
      <section id="what-we-automate" className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-white"></div>
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-gray-50 to-transparent transform -skew-y-1 origin-top"></div>
        
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                What We Automate
              </h2>
              <div className="inline-block w-24 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mb-6"></div>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
                We connect and automate a wide range of tools and systems to streamline your operations
              </p>
            </div>
            
            {/* Asymmetric Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {whatWeAutomate.map((item, index) => (
                <div 
                  key={index}
                  className={`group relative p-6 rounded-2xl transition-all duration-300 ${
                    index % 4 === 0 ? 'bg-gradient-to-br from-[#00d690]/10 to-white md:col-span-2 md:row-span-2' :
                    index % 4 === 1 ? 'bg-white border-2 border-[#00d690]/20' :
                    index % 4 === 2 ? 'bg-gradient-to-br from-white to-[#00b876]/10' :
                    'bg-white border-2 border-[#00b876]/20'
                  } hover:shadow-xl hover:scale-105 transform`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    index % 4 === 0 ? 'bg-[#00d690]' :
                    index % 4 === 1 ? 'bg-[#00d690]/20' :
                    index % 4 === 2 ? 'bg-[#00b876]/20' :
                    'bg-[#00b876]'
                  }`}>
                    <Icon 
                      name="Check" 
                      size={24} 
                      className={index % 4 === 0 || index % 4 === 3 ? 'text-white' : 'text-[#00d690]'} 
                    />
                  </div>
                  <p className={`font-semibold ${
                    index % 4 === 0 ? 'text-lg' : 'text-base'
                  } text-gray-800 group-hover:text-[#00d690] transition-colors`}>
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why OctalChip Section - Angled Design */}
      <section id="why-octalchip" className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#00d690]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00b876]/5 rounded-full blur-3xl"></div>
        
        {/* Diagonal Divider */}
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-br from-white to-transparent transform -skew-y-1 origin-top"></div>
        
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                Why OctalChip
              </h2>
              <div className="inline-block w-24 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mb-6"></div>
            </div>
            
            {/* Staggered Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {whyOctalChip.map((benefit, index) => (
                <div 
                  key={index}
                  className={`group relative p-6 md:p-8 rounded-2xl backdrop-blur-sm border border-white/10 bg-white/5 hover:bg-white/10 transition-all duration-300 ${
                    index % 2 === 0 ? 'transform md:translate-y-0' : 'transform md:-translate-y-4'
                  } hover:scale-105`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Icon name="Check" size={20} className="text-white" />
                    </div>
                    <p className="text-lg text-gray-200 group-hover:text-white transition-colors pt-1">
                      {benefit}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Curved Design */}
      <section id="cta" className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gray-50"></div>
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-gray-900 to-transparent transform -skew-y-1 origin-top"></div>
        
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-[#00d690]/20">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                Ready to Automate Your Workflows?
              </h2>
              <div className="inline-block w-24 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mb-6"></div>
              <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Let's discuss your automation needs and create workflows that eliminate manual work and improve efficiency. 
                Get a free consultation today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/contact#contact-form')}
                  className="bg-gradient-to-r from-[#00d690] to-[#00b876] hover:from-[#00b876] hover:to-[#00d690] text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Discuss Automation
                </button>
                <button
                  onClick={() => navigate('/services#services')}
                  className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-bold py-4 px-8 rounded-xl transition-all duration-300"
                >
                  View All Services
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={workflowAutomationFAQs}
        title="Workflow Automation FAQs"
        description="Common questions about workflow automation, n8n, Zapier, and Make integration services."
      />

      {/* Related Blogs Section */}
      <RelatedBlogs
        servicePath="/services/workflow-automation"
        serviceTags={['Workflow Automation', 'n8n', 'Zapier', 'Make', 'Automation', 'Integration', 'Business Process Automation']}
        serviceName="Workflow Automation"
        limit={6}
        title="Workflow Automation Related Blogs"
      />

      <Footer />
    </div>
  );
};

export default WorkflowAutomation;
